import React, { useMemo, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

/**
 * Comparador de duas avaliações.
 * Props: evaluations (array, ordenadas por data asc).
 */
export function EvaluationCompare({ evaluations = [] }) {
  const sorted = useMemo(() => [...evaluations].sort((a, b) => a.date.localeCompare(b.date)), [evaluations]);
  const [aId, setAId] = useState(sorted[0]?.id || "");
  const [bId, setBId] = useState(sorted[sorted.length - 1]?.id || "");

  React.useEffect(() => {
    if (sorted.length && !aId) setAId(sorted[0].id);
    if (sorted.length && !bId) setBId(sorted[sorted.length - 1].id);
  }, [sorted, aId, bId]);

  if (sorted.length < 2) {
    return (
      <div className="vv-card p-6 text-sm text-muted-foreground">
        É necessário ter ao menos 2 avaliações para comparar.
      </div>
    );
  }

  const a = sorted.find(e => e.id === aId);
  const b = sorted.find(e => e.id === bId);
  if (!a || !b) return null;

  const keys = Array.from(new Set([...Object.keys(a.scores || {}), ...Object.keys(b.scores || {})]));

  const summary = keys.reduce(
    (acc, k) => {
      const av = a.scores?.[k];
      const bv = b.scores?.[k];
      if (av != null && bv != null) {
        if (bv > av) acc.up++;
        else if (bv < av) acc.down++;
        else acc.same++;
      }
      return acc;
    },
    { up: 0, down: 0, same: 0 }
  );

  return (
    <div className="vv-card p-5" data-testid="eval-compare">
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Avaliação A</div>
          <Select value={aId} onValueChange={setAId}>
            <SelectTrigger data-testid="compare-a-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {sorted.map(e => <SelectItem key={e.id} value={e.id}>{e.date}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Avaliação B</div>
          <Select value={bId} onValueChange={setBId}>
            <SelectTrigger data-testid="compare-b-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {sorted.map(e => <SelectItem key={e.id} value={e.id}>{e.date}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4 text-center">
        <Tile label="Evoluções" value={summary.up} color="lime" icon={TrendingUp} />
        <Tile label="Estáveis" value={summary.same} color="yellow" icon={Minus} />
        <Tile label="Quedas" value={summary.down} color="red" icon={TrendingDown} />
      </div>

      <div className="overflow-hidden rounded-lg border border-border">
        <div className="grid grid-cols-12 bg-muted/40 text-xs uppercase tracking-wider font-bold px-3 py-2">
          <div className="col-span-5">Critério</div>
          <div className="col-span-2 text-center">A ({a.date.slice(5)})</div>
          <div className="col-span-2 text-center">B ({b.date.slice(5)})</div>
          <div className="col-span-3 text-right">Diferença</div>
        </div>
        {keys.map(k => {
          const av = a.scores?.[k] ?? null;
          const bv = b.scores?.[k] ?? null;
          const diff = (av != null && bv != null) ? bv - av : null;
          const color =
            diff == null ? "text-muted-foreground"
            : diff > 0 ? "text-[hsl(var(--vv-lime))]"
            : diff < 0 ? "text-destructive"
            : "text-muted-foreground";
          const Icon = diff == null ? Minus : diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
          return (
            <div key={k} className="grid grid-cols-12 items-center px-3 py-2 border-t border-border text-sm">
              <div className="col-span-5 font-semibold">{k}</div>
              <div className="col-span-2 text-center">{av ?? "—"}</div>
              <div className="col-span-2 text-center font-bold">{bv ?? "—"}</div>
              <div className={`col-span-3 flex items-center justify-end gap-1 font-black ${color}`}>
                <Icon className="size-4" />
                {diff == null ? "—" : (diff > 0 ? `+${diff}` : diff)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Tile({ label, value, icon: Icon, color }) {
  const c = {
    lime: "text-[hsl(var(--vv-lime))]",
    yellow: "text-[hsl(var(--vv-yellow))]",
    red: "text-destructive",
  }[color];
  return (
    <div className="rounded-lg bg-muted/30 border border-border p-3">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-3xl font-black ${c} flex items-center justify-center gap-1`}>
        <Icon className="size-5" /> {value}
      </div>
    </div>
  );
}
